import { RatingPopupOption } from '@/components/ratingAndReview/popup/ratingPopup.store';
import {
  deleteMyReviewItem,
  fetchMyReviewItem,
  fetchPublishedReviewItems,
} from '@/components/ratingAndReview/reviewList/reviewList.network';
import {
  IMyReviewItemResponse,
  IPublishedReviewItemsResponse,
  IReviewItem,
} from '@/components/ratingAndReview/typings/reviewItem.interface';
import { api } from '@/core';
import { fetchCountry } from '@/core/company';
import {
  getConfirmationInfo,
  isFreeTier,
  removeBot,
  requestBotConfirmationInfo,
  requestInstallBot,
} from '@/pages/botProvision';
import { IBotConfirmationInfo } from '@/pages/botProvision/typings';
import { fetchProfilesFromBE } from '@/pages/download/download.service';
import {
  requestAdditionalAppInfo,
  requestClientInfoWebUris,
  requestInstalledBots,
} from '@/pages/myApps/myApp.service';
import {
  IAdditionalAppInfo,
  IClientInfoWebUrisResponse,
  IRawBotItemsResponse,
} from '@/pages/myApps/typings';
import {
  fetchCollectionAppProfiles,
  fetchGalleryCollectionAppProfiles,
  fetchGalleryPageContent,
  fetchPageContent,
  fetchProfilesByAppCollection,
  getProfileByAppIdAndBrandId,
} from '@/pages/page/page.service';
import { loadGalleryPageForPreview } from '@/pages/page/preview/pagePreviewService';
import { IPagePreviewRequest } from '@/pages/page/preview/typings';
import { IPagePreviewResponse } from '@/pages/page/preview/typings/pagePreviewResponse.interface';
import { CollectionAppProfilesResponseInterface } from '@/pages/page/typings/collection-app-profiles-response.interface';
import { IPageContent } from '@/pages/page/typings/page-content.interface';
import { submitGuestForm, submitLeadForm } from '@/pages/profile';
import { loadProfileForPreview } from '@/pages/profile/preview/profilePreviewService';
import {
  loadProfile,
  loadProfileByBrand,
} from '@/pages/profile/profile.service';
import { loadCategory } from '@/pages/search';
import { loadMultipleApps } from '@/pages/search/filterBar/multipleAppsFilter';
import { searchIpaas } from '@/pages/search/ipaasSearch/ipaasSearch.service';
import { IpaasListResponseInterface } from '@/pages/search/ipaasSearch/typings';
import { searchProfiles } from '@/pages/search/profileSearch';
import { getBanner } from '@/shared/banner/event-banner.service';
import {
  loadGalleryPagesForAllBrands,
  loadPages,
  loadPagesForAllBrands,
} from '@/shared/navigation/pageNavigation';
import {
  ICoveoRequestForPages,
  ICoveoRequestForProfiles,
  requestCoveoSearchFlag,
  ICoveoResponseForPages,
  ICoveoResponseForProfiles,
} from '@/shared/searchProvider';
import * as coveoCloudService from '@/shared/searchProvider/coveo/coveoNetworkService';
import {
  IGalleryBrand,
  IGalleryBrandsLoaderOptions,
  loadGalleryBrands,
} from '@/stores/compatibility/compatibilities/galleryBrand';
import { getAdditionalAccountInfo, getExtension, getUser } from '@/stores/user';
import { TransportInterface } from '@/transport.interface';
import {
  IPagesForAllBrandsResponse,
  IProfile,
  PagesInterface,
  ProfileListResponseInterface,
} from '@/types';
import { IProfileByAppAndBrandResponse } from '@/types/getProfileByAppAndBrandResponse.interface';
import { ProfileListRequestInterface } from '@/types/profileListRequest.interface';

export class TransportOnAPW implements TransportInterface {
  public fetchGalleryBrands(
    options?: IGalleryBrandsLoaderOptions,
  ): Promise<IGalleryBrand[]> {
    return loadGalleryBrands(options);
  }

  public fetchUser() {
    return getUser();
  }

  public fetchExtension() {
    return getExtension();
  }

  public fetchAdditionalAccountInfo() {
    return getAdditionalAccountInfo();
  }

  public fetchCompany() {
    return fetchCountry();
  }

  public fetchProfile(
    vanityUrl: string,
    useGlobalLoading?: boolean,
  ): Promise<IProfile> {
    return loadProfile(vanityUrl, useGlobalLoading);
  }

  public fetchProfileByBrand(
    vanityUrl: string,
    brand: string,
    useGlobalLoading?: boolean,
  ): Promise<IProfile> {
    return loadProfileByBrand(vanityUrl, brand, useGlobalLoading);
  }

  public fetchProfileForPreview(
    profileId: string,
    timestamp: string,
    token: string,
    type: string,
    brandId?: string,
  ): Promise<IProfile> {
    return loadProfileForPreview(profileId, timestamp, token, type, brandId);
  }

  public fetchPages(brandId: string): Promise<PagesInterface> {
    return loadPages(brandId);
  }

  public fetchPagesForAllBrands(): Promise<IPagesForAllBrandsResponse> {
    return loadPagesForAllBrands();
  }

  public fetchGalleryPagesForAllBrands(): Promise<IPagesForAllBrandsResponse> {
    return loadGalleryPagesForAllBrands();
  }

  public fetchGalleryPageForPreview(
    request: IPagePreviewRequest,
  ): Promise<IPagePreviewResponse> {
    return loadGalleryPageForPreview(request);
  }

  public fetchPageContent(pageId: number) {
    return fetchPageContent(pageId);
  }

  public fetchGalleryPageContent(
    pageId: number,
    brandId: string,
  ): Promise<IPageContent> {
    return fetchGalleryPageContent(pageId, brandId);
  }

  public fetchCollectionAppProfiles(
    id: number,
    page: number,
    perPage?: number,
  ) {
    return fetchCollectionAppProfiles(id, page, perPage);
  }

  public fetchProfilesByAppCollection(
    id: number,
    brandId: string,
    page: number,
    perPage?: number,
  ) {
    return fetchProfilesByAppCollection(id, brandId, page, perPage);
  }

  public fetchGalleryCollectionAppProfiles(
    id: number,
    brandId: string,
    page: number,
    perPage: number,
  ): Promise<CollectionAppProfilesResponseInterface> {
    return fetchGalleryCollectionAppProfiles(id, brandId, page, perPage);
  }

  public fetchBotConfirmationInfo(
    appId: string,
  ): Promise<IBotConfirmationInfo> {
    return requestBotConfirmationInfo(appId);
  }

  public fetchIsFreeTier(): Promise<boolean> {
    return isFreeTier();
  }

  public fetchInstallBot(
    clientId: string,
    botName: string,
    customerParameter?: string,
  ) {
    return requestInstallBot(clientId, botName, customerParameter);
  }

  public searchProfiles(
    request: ProfileListRequestInterface,
    options?: { useGlobalLoading?: boolean },
  ): Promise<ProfileListResponseInterface> {
    return searchProfiles(request, options);
  }

  public fetchMultipleApps(
    request: ProfileListRequestInterface,
  ): Promise<ProfileListResponseInterface> {
    return loadMultipleApps(request);
  }

  public searchIpaas(
    searchText: string,
    brandId: string,
    page: number,
  ): Promise<IpaasListResponseInterface> {
    return searchIpaas(searchText, brandId, page);
  }

  public removeBot(clientId: string) {
    return removeBot(clientId);
  }

  public getConfirmationInfo(appId: string): Promise<IBotConfirmationInfo> {
    return getConfirmationInfo(appId);
  }

  public fetchClientInfoWebUris(): Promise<IClientInfoWebUrisResponse> {
    return requestClientInfoWebUris();
  }

  public fetchInstalledBots(): Promise<IRawBotItemsResponse> {
    return requestInstalledBots();
  }

  public fetchAdditionalAppInfo(
    botExtensionId: string,
  ): Promise<IAdditionalAppInfo> {
    return requestAdditionalAppInfo(botExtensionId);
  }

  public fetchCategories(brandId: string, product: string): Promise<string[]> {
    return loadCategory(brandId, product);
  }

  public submitLeadForm(
    appProfileId: string,
    appProfileName: string,
    values: any,
  ): Promise<any> {
    return submitLeadForm(appProfileId, appProfileName, values);
  }

  public submitGuestForm(appProfileId: string, values: any): Promise<any> {
    return submitGuestForm(appProfileId, values);
  }

  public getBanner() {
    return getBanner();
  }

  public getProfileByAppIdAndBrandId(
    appId: string,
    brandId: string,
  ): Promise<IProfileByAppAndBrandResponse> {
    return getProfileByAppIdAndBrandId(appId, brandId);
  }

  public requestCoveoSearchFlag() {
    return requestCoveoSearchFlag();
  }

  searchPagesFromCoveo(
    request: ICoveoRequestForPages,
  ): Promise<ICoveoResponseForPages> {
    return coveoCloudService.searchPages(request);
  }

  searchProfilesFromCoveo(
    request: ICoveoRequestForProfiles,
  ): Promise<ICoveoResponseForProfiles> {
    return coveoCloudService.searchProfiles(request);
  }

  public fetchProfilesToDownload(
    brandId: string,
    app?: string,
    osList?: string[],
  ): Promise<IProfile[]> {
    return fetchProfilesFromBE(brandId, app, osList);
  }

  fetchMyReviewItem(applicationId: string): Promise<IMyReviewItemResponse> {
    return fetchMyReviewItem(applicationId);
  }

  fetchPublishedReviewItems(
    applicationId: string,
    page: number,
  ): Promise<IPublishedReviewItemsResponse> {
    return fetchPublishedReviewItems(applicationId, page);
  }

  saveReview(
    applicationId: string,
    review: RatingPopupOption,
  ): Promise<IReviewItem> {
    return api.put<IReviewItem>(
      `/api/rating-review/applications/${applicationId}/reviews/my-review`,
      review,
      {
        useGlobalLoading: true,
      },
    );
  }

  deleteMyReviewItem(applicationId: string): Promise<void> {
    return deleteMyReviewItem(applicationId);
  }
}
